// src/pages/InventoryManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import nooLogo from '../assets/noo.png';
import leftArrowIcon from '../assets/left.png';

const displayFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };
const bodyFont = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const monoFont = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" };

const InventoryManagement = () => {
  const [inventoryItems, setInventoryItems] = useState(() => {
    const savedInventory = localStorage.getItem('clinicInventory');
    return savedInventory ? JSON.parse(savedInventory) : [
      { id: 'item1', name: 'Disposable Gloves (Box of 100)', quantity: 50, unit: 'box', expiryDate: '2025-12-31', supplier: 'Medical Supplies Inc.', price: 15.00, minThreshold: 10 },
      { id: 'item2', name: 'Antiseptic Solution (500ml)', quantity: 5, unit: 'bottle', expiryDate: '2024-09-30', supplier: 'PharmaDistributors', price: 25.50, minThreshold: 3 },
      { id: 'item3', name: 'Pain Reliever Tablets (Bottle of 100)', quantity: 8, unit: 'bottle', expiryDate: '2026-06-30', supplier: 'Generic Meds Ltd.', price: 45.00, minThreshold: 10 },
      { id: 'item4', name: 'Syringes (Box of 50)', quantity: 20, unit: 'box', expiryDate: '2025-10-15', supplier: 'Medical Supplies Inc.', price: 30.00, minThreshold: 5 },
    ];
  });

  useEffect(() => {
    localStorage.setItem('clinicInventory', JSON.stringify(inventoryItems));
  }, [inventoryItems]);

  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [formName, setFormName] = useState('');
  const [formQuantity, setFormQuantity] = useState('');
  const [formUnit, setFormUnit] = useState('');
  const [formExpiryDate, setFormExpiryDate] = useState('');
  const [formSupplier, setFormSupplier] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formMinThreshold, setFormMinThreshold] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');

  const filteredAndSearchedItems = inventoryItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.supplier.toLowerCase().includes(searchTerm.toLowerCase());

    const isLowStockItem = item.quantity <= item.minThreshold;
    const isExpiringSoonItem = new Date(item.expiryDate) < new Date(new Date().setMonth(new Date().getMonth() + 3));

    if (filterType === 'Low Stock') return matchesSearch && isLowStockItem;
    if (filterType === 'Expiring Soon') return matchesSearch && isExpiringSoonItem;
    return matchesSearch;
  });

  const handleAddItem = () => {
    setCurrentItem(null);
    setFormName('');
    setFormQuantity('');
    setFormUnit('');
    setFormExpiryDate('');
    setFormSupplier('');
    setFormPrice('');
    setFormMinThreshold('');
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setCurrentItem(item);
    setFormName(item.name);
    setFormQuantity(item.quantity);
    setFormUnit(item.unit);
    setFormExpiryDate(item.expiryDate);
    setFormSupplier(item.supplier);
    setFormPrice(item.price);
    setFormMinThreshold(item.minThreshold);
    setShowModal(true);
  };

  const handleSubmitItem = (e) => {
    e.preventDefault();
    const newItem = {
      id: currentItem ? currentItem.id : `inv${Date.now()}`,
      name: formName,
      quantity: parseInt(formQuantity),
      unit: formUnit,
      expiryDate: formExpiryDate,
      supplier: formSupplier,
      price: parseFloat(formPrice),
      minThreshold: parseInt(formMinThreshold),
    };

    if (currentItem) {
      setInventoryItems(inventoryItems.map(item => item.id === newItem.id ? newItem : item));
    } else {
      setInventoryItems([newItem, ...inventoryItems]);
    }
    setShowModal(false);
  };

  const handleDeleteItem = (id) => {
    if (window.confirm('Are you sure you want to delete this item from inventory?')) {
      setInventoryItems(inventoryItems.filter(item => item.id !== id));
    }
  };

  const isLowStock = (item) => item.quantity <= item.minThreshold;

  const isExpiringSoon = (item) => {
    const expiry = new Date(item.expiryDate);
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return expiry < threeMonthsFromNow && expiry >= new Date();
  };

  const isExpired = (item) => new Date(item.expiryDate) < new Date();

  const lowStockItems = inventoryItems.filter(isLowStock);
  const expiringSoonItems = inventoryItems.filter(isExpiringSoon);
  const expiredItems = inventoryItems.filter(isExpired);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" style={bodyFont}>
      {/* Header */}
      <div className="bg-white border-b border-[#E4E1D8]">
        <div className="container mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/home">
              <img src={nooLogo} alt="Drogastro" className="w-12 h-12 object-contain" />
            </Link>
            <div>
              <p className="text-[11px] tracking-wider text-[#0F3D3E]/50" style={monoFont}>SUPPLIES</p>
              <h1 className="text-xl md:text-2xl font-bold text-[#0F3D3E]" style={displayFont}>Inventory Management</h1>
            </div>
          </div>
          <Link
            to="/home"
            className="flex items-center gap-2 bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#0F3D3E] font-semibold py-2 px-4 rounded-md border border-[#E4E1D8] transition-colors duration-200 text-sm"
          >
            <img src={leftArrowIcon} alt="Back" className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>TOTAL ITEMS</p>
            <p className="text-2xl font-bold text-[#0F3D3E]" style={displayFont}>{inventoryItems.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>LOW STOCK</p>
            <p className="text-2xl font-bold text-[#D6A419]" style={displayFont}>{lowStockItems.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>EXPIRING SOON</p>
            <p className="text-2xl font-bold text-orange-600" style={displayFont}>{expiringSoonItems.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>EXPIRED</p>
            <p className="text-2xl font-bold text-red-600" style={displayFont}>{expiredItems.length}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-sm p-5 md:p-8">
          {/* Alerts */}
          {(lowStockItems.length > 0 || expiringSoonItems.length > 0 || expiredItems.length > 0) && (
            <div className="mb-6 space-y-3">
              {lowStockItems.length > 0 && (
                <div className="flex items-start gap-3 bg-[#D6A419]/10 border-l-4 border-[#D6A419] text-[#8a6b13] p-4 rounded-md">
                  <span className="w-2 h-2 rounded-full bg-[#D6A419] mt-1.5 shrink-0" />
                  <p className="text-sm"><span className="font-bold">Low stock:</span> {lowStockItems.map(item => item.name).join(', ')}.</p>
                </div>
              )}
              {expiringSoonItems.length > 0 && (
                <div className="flex items-start gap-3 bg-orange-50 border-l-4 border-orange-400 text-orange-700 p-4 rounded-md">
                  <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                  <p className="text-sm"><span className="font-bold">Expiring soon:</span> {expiringSoonItems.map(item => item.name).join(', ')}.</p>
                </div>
              )}
              {expiredItems.length > 0 && (
                <div className="flex items-start gap-3 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />
                  <p className="text-sm"><span className="font-bold">Expired:</span> {expiredItems.map(item => item.name).join(', ')}.</p>
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-6 gap-4">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <input
                type="text"
                placeholder="Search by name or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="p-2 border border-[#E4E1D8] rounded-md w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
              />
              <label htmlFor="filter-type" className="text-xs font-semibold text-[#0F3D3E]/70 whitespace-nowrap" style={monoFont}>FILTER</label>
              <select
                id="filter-type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="p-2 border border-[#E4E1D8] rounded-md w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
              >
                <option value="All">All Items</option>
                <option value="Low Stock">Low Stock</option>
                <option value="Expiring Soon">Expiring Soon</option>
              </select>
            </div>
            <button
              onClick={handleAddItem}
              className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200 w-full md:w-auto"
            >
              + Add New Item
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-[#0F3D3E]" style={displayFont}>Current Inventory</h2>
            <span className="text-xs font-semibold text-[#0F3D3E]/50 bg-[#FAF9F6] border border-[#E4E1D8] rounded-full px-3 py-1" style={monoFont}>
              {filteredAndSearchedItems.length} SHOWN
            </span>
          </div>

          {filteredAndSearchedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-14 px-6 bg-[#FAF9F6] rounded-lg border border-dashed border-[#E4E1D8]">
              <p className="text-[#22262B]/60 text-base">No inventory items found matching the criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-[#E4E1D8]">
              <table className="min-w-full bg-white">
                <thead className="bg-[#0F3D3E]/5">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Item Name</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Qty</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Unit</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Expiry</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Supplier</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Price</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Status</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSearchedItems.map(item => (
                    <tr key={item.id} className="border-t border-[#E4E1D8] hover:bg-[#FAF9F6] transition-colors">
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B] font-medium">{item.name}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]">{item.quantity}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]/80">{item.unit}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]/80">{item.expiryDate}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]/80">{item.supplier}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#0F3D3E] font-semibold">{item.price.toFixed(2)} EGP</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isExpired(item) ? 'bg-red-100 text-red-700' :
                          isLowStock(item) ? 'bg-[#D6A419]/20 text-[#8a6b13]' :
                          isExpiringSoon(item) ? 'bg-orange-100 text-orange-700' : 'bg-[#4FB3A9]/15 text-[#0F3D3E]'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            isExpired(item) ? 'bg-red-500' :
                            isLowStock(item) ? 'bg-[#D6A419]' :
                            isExpiringSoon(item) ? 'bg-orange-500' : 'bg-[#4FB3A9]'
                          }`} />
                          {isExpired(item) ? 'Expired' : isLowStock(item) ? 'Low Stock' : isExpiringSoon(item) ? 'Expiring Soon' : 'In Stock'}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <button onClick={() => handleEditItem(item)} className="text-[#0F3D3E] hover:text-[#4FB3A9] font-medium mr-4 transition-colors">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteItem(item.id)} className="text-red-600 hover:text-red-800 font-medium transition-colors">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Item Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-[#0F3D3E]/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-8 pt-7 pb-5 border-b border-[#E4E1D8] sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-[#0F3D3E]" style={displayFont}>
                {currentItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}
              </h2>
            </div>
            <form onSubmit={handleSubmitItem} className="space-y-4 px-8 py-6">
              <div>
                <label htmlFor="item-name" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>ITEM NAME</label>
                <input
                  type="text"
                  id="item-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="item-quantity" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>QUANTITY</label>
                  <input
                    type="number"
                    id="item-quantity"
                    value={formQuantity}
                    onChange={(e) => setFormQuantity(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="item-unit" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>UNIT</label>
                  <input
                    type="text"
                    id="item-unit"
                    value={formUnit}
                    onChange={(e) => setFormUnit(e.target.value)}
                    placeholder="box, bottle..."
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="item-expiry-date" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>EXPIRY DATE</label>
                <input
                  type="date"
                  id="item-expiry-date"
                  value={formExpiryDate}
                  onChange={(e) => setFormExpiryDate(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                />
              </div>
              <div>
                <label htmlFor="item-supplier" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>SUPPLIER</label>
                <input
                  type="text"
                  id="item-supplier"
                  value={formSupplier}
                  onChange={(e) => setFormSupplier(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="item-price" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>PRICE (EGP)</label>
                  <input
                    type="number"
                    id="item-price"
                    value={formPrice}
                    onChange={(e) => setFormPrice(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="item-min-threshold" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>MIN THRESHOLD</label>
                  <input
                    type="number"
                    id="item-min-threshold"
                    value={formMinThreshold}
                    onChange={(e) => setFormMinThreshold(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#22262B] font-semibold py-2.5 px-5 rounded-md border border-[#E4E1D8] transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200"
                >
                  {currentItem ? 'Save Changes' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
